import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAgencies,
  setSearchQuery,
  setCurrentPage,
  setRowsPerPage,
  selectAgencyData,
  selectAgencyLoading,
  selectAgencyError,
  selectAgencyTotalPages,
  selectAgencyCurrentPage,
  selectAgencyRowsPerPage,
  selectAgencySearchQuery,
} from "../../../inspections/components/AgencyList/agencySlice";

// BC-styled components
import ListPageHeader from "../../../../components/Tables/ListPageControls/ListPageHeader";
import ListPagination from "../../../../components/Tables/Pagination/ListPagination";
import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";

// The modal that supports next/prev
import ViewAgencyModal from "./ViewAgencyModal";
import AddAgencyModal from "./AddAgencyModal";

const AgencyDetailsList = () => {
  const dispatch = useDispatch();

  // Redux state
  const agencyDetails = useSelector(selectAgencyData);
  const isLoading = useSelector(selectAgencyLoading);
  const error = useSelector(selectAgencyError);
  const totalPages = useSelector(selectAgencyTotalPages);
  const currentPage = useSelector(selectAgencyCurrentPage);
  const rowsPerPage = useSelector(selectAgencyRowsPerPage);
  const searchQuery = useSelector(selectAgencySearchQuery);

  // Local UI state
  const [viewMode, setViewMode] = useState("table");
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // Navigation height (adjust as needed)
  const navHeight = 64; // Replace with the actual height of your navigation bar

  // Function to update the selected agency
  const handleSetAgency = (updatedAgency) => {
    setSelectedAgency(updatedAgency);
  };

  // Fetch data whenever page/rows/search changes
  useEffect(() => {
    dispatch(
      fetchAgencies({
        page: currentPage,
        size: rowsPerPage,
        query: searchQuery,
      })
    );
  }, [dispatch, currentPage, rowsPerPage, searchQuery]);

  // Handlers
  const handleSearchChange = (e) => {
    dispatch(setSearchQuery(e.target.value));
  };

  const handleSearch = () => {
    dispatch(setCurrentPage(1));
    dispatch(
      fetchAgencies({
        page: 1,
        size: rowsPerPage,
        query: searchQuery,
      })
    );
  };

  const handlePageChange = (_, page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (e) => {
    dispatch(setRowsPerPage(Number(e.target.value)));
    dispatch(setCurrentPage(1));
  };

  const handleOpenViewModal = (agency) => {
    setSelectedAgency(agency);
    setIsViewOpen(true);
  };

  return (
    <div className="flex flex-col h-screen w-full">
      {/* Header */}
      <ListPageHeader
        title="Agency List"
        searchQuery={searchQuery}
        onSearchInputChange={handleSearchChange}
        onSearch={handleSearch}
        viewMode={viewMode}
        onToggleViewMode={setViewMode}
        onAdd={() => setIsCreateOpen(true)}
      />

      {/* Error state */}
      {error && <div className="p-4 text-red-500">Error: {error}</div>}

      {/* Main Content */}
      {isLoading ? (
        <div className="p-4">Loading...</div>
      ) : (
        <div className="flex-1 overflow-auto">
          {agencyDetails && agencyDetails.length > 0 ? (
            <>
              {viewMode === "table" && (
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 border-b">View</th>
                      <th className="px-4 py-2 border-b">Agency ID</th>
                      <th className="px-4 py-2 border-b">Name</th>
                      <th className="px-4 py-2 border-b">Type</th>
                      <th className="px-4 py-2 border-b">Track Tests</th>
                      <th className="px-4 py-2 border-b">Track Violations</th>
                      <th className="px-4 py-2 border-b">Proposal Class</th>
                      <th className="px-4 py-2 border-b">Dispatch Class</th>
                      <th className="px-4 py-2 border-b">Address Line 1</th>
                      <th className="px-4 py-2 border-b">City</th>
                      <th className="px-4 py-2 border-b">State</th>
                      <th className="px-4 py-2 border-b">Zip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agencyDetails.map((agency) => (
                      <tr key={agency.agencyId} className="hover:bg-gray-100">
                        <td className="px-4 py-2 border-b">
                          <button
                            className="text-blue-600 hover:underline"
                            onClick={() => handleOpenViewModal(agency)}
                          >
                            View
                          </button>
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.agencyId}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.agencyName}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.agencyType}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.trackTests ? "Yes" : "No"}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.trackViolations ? "Yes" : "No"}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.proposalClass}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.dispatchClass}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {agency.addressLine1}
                        </td>
                        <td className="px-4 py-2 border-b">{agency.city}</td>
                        <td className="px-4 py-2 border-b">{agency.state}</td>
                        <td className="px-4 py-2 border-b">{agency.zip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {viewMode === "card" && (
                <div className="grid gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {agencyDetails.map((agency) => (
                    <div
                      key={agency.agencyId}
                      className="p-4 border rounded-lg shadow hover:shadow-md"
                    >
                      <h3 className="font-bold">{agency.agencyName}</h3>
                      <p>ID: {agency.agencyId}</p>
                      <p>Type: {agency.agencyType}</p>
                      <button
                        className="text-blue-600 hover:underline mt-2"
                        onClick={() => handleOpenViewModal(agency)}
                      >
                        View
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="p-4">No agencies found.</div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {!isLoading && agencyDetails.length > 0 && (
        <div className="flex justify-between items-center p-4 border-t border-gray-300 bg-white">
          <ListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      )}

      {/* View Modal */}
      <ViewAgencyModal
        open={isViewOpen}
        handleClose={() => setIsViewOpen(false)}
        selectedAgency={selectedAgency}
        setSelectedAgency={setSelectedAgency}
        agency={selectedAgency}
        setAgency={handleSetAgency}
        agencies={agencyDetails}
        allAgencies={agencyDetails}
        navHeight={64} // Pass the actual navigation height here
        updateAll={(updatedAgencies) => {
          // Update the Redux state or local state with the new agencies
        }}
      />

      {/* Create Modal */}
      <AddAgencyModal
        open={isCreateOpen}
        handleClose={() => setIsCreateOpen(false)}
      />
    </div>
  );
};

export default AgencyDetailsList;
